import {
  buildSemanticEnumValueEnum,
  getSemanticEnumTagColor,
  type SemanticEnumRegistry,
  type SemanticEnumValueEnumRecord,
} from "./semantic-enum";

export const REMOTE_SESSION_STATUS_VALUES = ["pending", "active", "closed", "failed"] as const;

export type CanonicalRemoteSessionStatus = (typeof REMOTE_SESSION_STATUS_VALUES)[number];

export type RemoteSessionTagColor = "processing" | "success" | "default" | "error" | "warning";

export const REMOTE_SESSION_STATUS_PRESENTATION_REGISTRY = {
  pending: { tone: "processing" },
  active: { tone: "success" },
  closed: { tone: "default" },
  failed: { tone: "error" },
} as const satisfies SemanticEnumRegistry<CanonicalRemoteSessionStatus>;

export function readRemoteSessionStatusText(value: unknown) {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
}

export function normalizeRemoteSessionStatus(value: unknown) {
  return readRemoteSessionStatusText(value);
}

export function getCanonicalRemoteSessionStatus(
  value: unknown,
): CanonicalRemoteSessionStatus | null {
  const normalized = normalizeRemoteSessionStatus(value);
  return REMOTE_SESSION_STATUS_VALUES.includes(normalized as CanonicalRemoteSessionStatus)
    ? (normalized as CanonicalRemoteSessionStatus)
    : null;
}

export function getRemoteSessionStatusTagColor(value: unknown): RemoteSessionTagColor {
  return getSemanticEnumTagColor(
    REMOTE_SESSION_STATUS_PRESENTATION_REGISTRY,
    normalizeRemoteSessionStatus(value),
    "warning",
  ) as RemoteSessionTagColor;
}

export function isTerminalRemoteSessionStatus(value: unknown) {
  const canonicalStatus = getCanonicalRemoteSessionStatus(value);
  return canonicalStatus === "closed" || canonicalStatus === "failed";
}

export function isOpenRemoteSessionStatus(value: unknown) {
  const canonicalStatus = getCanonicalRemoteSessionStatus(value);
  return canonicalStatus === "pending" || canonicalStatus === "active";
}

export function buildRemoteSessionStatusSemanticValueEnum(
  getLabel: (value: CanonicalRemoteSessionStatus) => string,
): SemanticEnumValueEnumRecord {
  return buildSemanticEnumValueEnum(
    REMOTE_SESSION_STATUS_PRESENTATION_REGISTRY,
    REMOTE_SESSION_STATUS_VALUES,
    getLabel,
  );
}
